import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Pagination,
  Popconfirm,
  Select,
  Spin,
  Switch,
  Tabs,
  Tag,
  Tree,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { Col, Container, Row, Table } from "react-bootstrap";
import { BiEdit } from "react-icons/bi";
import { IoTrashOutline } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import ImagePicker2 from "../../../component/image-pickersecond";
import Loadingbox from "../../../component/loadingbox";
import PageHeader from "../../../component/pageHeaders";
import { API } from "../../../config/API";
import { uploadImageToS3 } from "../../../config/s3config/s3fileUpload";
import { GET, POST, PUT } from "../../../utils/apirequest";
import HighlightForm from "../components/highlightForm";

/**
 * Unified Election Data screen — group tree, locations under selected group,
 * candidate inline editor + batch vote entry, and a quick view of the
 * derived seat tally / progress. One screen for the newsroom on counting day.
 */

const STATUS_OPTIONS = [
  { value: "not_started", label: "Not started" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
];

const STATUS_META: Record<string, { label: string; color: string }> = {
  not_started: { label: "Not started", color: "default" },
  in_progress: { label: "Counting", color: "processing" },
  completed: { label: "Completed", color: "success" },
};

function renderCountingStatus(value?: string) {
  const meta = STATUS_META[value || "not_started"] || {
    label: value || "—",
    color: "default",
  };
  return (
    <Tag color={meta.color} style={{ fontWeight: 500 }}>
      {meta.label}
    </Tag>
  );
}

function ElectionDataScreen() {
  const { EID: EIDRaw } = useParams();
  const EID = EIDRaw ? Number(EIDRaw) : null;
  const navigate = useNavigate();

  const [tab, setTab] = useState<"data" | "reports">("data");
  const [loading, setLoading] = useState(true);
  const [masterTitle, setMasterTitle] = useState<string>("");
  const [groupTree, setGroupTree] = useState<any[]>([]);
  const [groupsFlat, setGroupsFlat] = useState<any[]>([]);
  const [parties, setParties] = useState<any[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [locations, setLocations] = useState<any[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [progress, setProgress] = useState<any>({});
  const [tally, setTally] = useState<any[]>([]);

  const [groupModal, setGroupModal] = useState<{ open: boolean; row?: any; parentId?: number | null }>({ open: false });
  const [locationModal, setLocationModal] = useState<{ open: boolean; row?: any }>({ open: false });
  const [candidatesModal, setCandidatesModal] = useState<{ open: boolean; location?: any }>({ open: false });
  const [highlightModal, setHighlightModal] = useState<{ open: boolean }>({ open: false });
  const [highlightSaving, setHighlightSaving] = useState(false);
  const [highlight, setHighlight] = useState<any | null>(null);

  // ── per-action loading flags ───────────────────────────────────────────
  const [groupSaving, setGroupSaving] = useState(false);
  const [locationSaving, setLocationSaving] = useState(false);
  const [deletingGroupId, setDeletingGroupId] = useState<number | null>(null);
  const [deletingLocationId, setDeletingLocationId] = useState<number | null>(null);

  // ── boot ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!EID) return;
    (async () => {
      try {
        await Promise.all([loadMaster(), loadGroups(), loadParties(), loadProgress(), loadTally(), loadHighlight()]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [EID]);

  useEffect(() => {
    if (selectedGroupId) loadLocationsForGroup(selectedGroupId);
    else setLocations([]);
  }, [selectedGroupId]);

  const loadMaster = async () => {
    try {
      const res: any = await GET(API.ELECTION_MASTER_DETAILS + EID, null, null);
      const title = res?.data?.attributes?.title || res?.data?.title || "";
      setMasterTitle(title);
    } catch {
      setMasterTitle("");
    }
  };

  const loadGroups = async () => {
    const flat: any = await POST(API.ELECTION_GROUP_LIST, { masterId: EID, limit: 5000 });
    setGroupsFlat(flat?.data || []);
    const tree: any = await GET(API.ELECTION_GROUP_TREE + `?masterId=${EID}`, null, null);
    setGroupTree(tree?.data || []);
  };

  const loadParties = async () => {
    const res: any = await POST(API.ELECTION_PARTY_LIST, { masterId: EID, limit: 5000 });
    setParties(res?.data || []);
  };

  // Load the (singleton, by convention) highlight item for this master so the
  // "+ Highlights" button edits the existing row instead of creating duplicates.
  const loadHighlight = async () => {
    try {
      const res: any = await POST(API.ELECTION_ITEMS_LIST, {
        masterId: EID,
        type: "highlight",
        limit: 1,
      });
      setHighlight(res?.data?.[0] || null);
    } catch {
      setHighlight(null);
    }
  };

  const loadLocationsForGroup = async (groupId: number) => {
    setLocationsLoading(true);
    try {
      const res: any = await POST(API.ELECTION_LOCATION_LIST, { masterId: EID, groupId, limit: 5000 });
      setLocations(res?.data || []);
    } catch (err: any) {
      message.error(err?.message || "Failed to load locations");
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  };

  const loadProgress = async () => {
    const res: any = await GET(API.ELECTION_REPORT_PROGRESS + `?masterId=${EID}`, null, null);
    setProgress(res?.data || {});
  };

  const loadTally = async () => {
    const res: any = await GET(API.ELECTION_REPORT_TALLY + `?masterId=${EID}`, null, null);
    setTally(res?.data || []);
  };

  // ── group ops ──────────────────────────────────────────────────────────
  const saveGroup = async (values: any) => {
    setGroupSaving(true);
    try {
      const payload = {
        data: {
          name: values.name,
          slug: values.slug || values.name?.toLowerCase()?.replace(/\s+/g, "-"),
          parent: values.parent || null,
          sort_order: Number(values.sort_order) || 0,
          is_featured: !!values.is_featured,
          election_master: EID,
          is_deleted: false,
        },
      };
      if (groupModal.row?.id) {
        await PUT(API.ELECTION_GROUP_EDIT + groupModal.row.id, payload);
      } else {
        await POST(API.ELECTION_GROUP_ADD, payload);
      }
      message.success("Group saved");
      setGroupModal({ open: false });
      await loadGroups();
    } catch (err: any) {
      message.error(err?.message || "Failed to save group");
    } finally {
      setGroupSaving(false);
    }
  };

  const deleteGroup = async (id: number) => {
    setDeletingGroupId(id);
    try {
      await PUT(API.ELECTION_GROUP_EDIT + id, { data: { is_deleted: true } });
      message.success("Group moved to trash");
      await loadGroups();
      if (selectedGroupId === id) setSelectedGroupId(null);
    } catch (err: any) {
      message.error(err?.message || "Failed to delete group");
    } finally {
      setDeletingGroupId(null);
    }
  };

  // ── location ops ───────────────────────────────────────────────────────
  const saveLocation = async (values: any) => {
    setLocationSaving(true);
    try {
      const payload = {
        data: {
          name: values.name,
          code: values.code || null,
          group: selectedGroupId,
          election_master: EID,
          total_voters: String(values.total_voters || 0),
          total_votes_polled: String(values.total_votes_polled || 0),
          counting_status: values.counting_status || "not_started",
          sort_order: Number(values.sort_order) || 0,
          is_featured: !!values.is_featured,
          is_deleted: false,
        },
      };
      if (locationModal.row?.id) {
        await PUT(API.ELECTION_LOCATION_EDIT + locationModal.row.id, payload);
      } else {
        await POST(API.ELECTION_LOCATION_ADD, payload);
      }
      message.success("Location saved");
      setLocationModal({ open: false });
      await loadLocationsForGroup(selectedGroupId!);
      await Promise.all([loadProgress(), loadTally()]);
    } catch (err: any) {
      message.error(err?.message || "Failed to save location");
    } finally {
      setLocationSaving(false);
    }
  };

  // ── highlights ─────────────────────────────────────────────────────────
  const saveHighlight = async (values: any) => {
    setHighlightSaving(true);
    try {
      const payload = {
        data: {
          title: values.title,
          type: "highlight",
          data: values.data || [],
          election_master: EID,
        },
      };
      const existingId = highlight?.id;
      const res: any = existingId
        ? await PUT(API.ELECTION_ITEMS_EDIT + existingId, payload)
        : await POST(API.ELECTION_ITEMS_ADD, payload);
      if (res?.data?.id) {
        message.success(existingId ? "Highlights updated" : "Highlights saved");
        setHighlightModal({ open: false });
        await loadHighlight();
      } else {
        message.warning("Highlights not saved — please retry");
      }
    } catch (err: any) {
      message.error(err?.message || "Failed to save highlights");
    } finally {
      setHighlightSaving(false);
    }
  };

  const deleteLocation = async (id: number) => {
    setDeletingLocationId(id);
    try {
      await PUT(API.ELECTION_LOCATION_EDIT + id, { data: { is_deleted: true } });
      message.success("Location moved to trash");
      await loadLocationsForGroup(selectedGroupId!);
      await Promise.all([loadProgress(), loadTally()]);
    } catch (err: any) {
      message.error(err?.message || "Failed to delete location");
    } finally {
      setDeletingLocationId(null);
    }
  };

  // ── candidates: load + batch save ──────────────────────────────────────
  const [openingCandsForId, setOpeningCandsForId] = useState<number | null>(null);
  const openCandidates = async (loc: any) => {
    setOpeningCandsForId(loc.id);
    try {
      const res: any = await GET(API.ELECTION_LOCATION_FULL + `${loc.id}/full`, null, null);
      setCandidatesModal({ open: true, location: res?.data || loc });
    } catch (err: any) {
      message.error(err?.message || "Failed to load candidates");
    } finally {
      setOpeningCandsForId(null);
    }
  };

  // ── tree props for antd Tree ───────────────────────────────────────────
  const treeData = useMemo(() => {
    const toNode = (g: any): any => ({
      key: g.id,
      title: g.name,
      children: (g.children || []).map(toNode),
    });
    return groupTree.map(toNode);
  }, [groupTree]);

  if (loading) {
    return (
      <div style={{ height: "90vh", overflow: "scroll" }}>
        <PageHeader title="Election Data" length="loading" />
        <Loadingbox />
      </div>
    );
  }

  return (
    <div style={{ height: "90vh", overflow: "auto" }}>
      <PageHeader title="Election Data" length={masterTitle || `Master #${EID}`}>
        <div className="homescreen-pageItem" style={{ display: "flex", gap: 8 }}>
          <Button type="primary" onClick={() => setHighlightModal({ open: true })}>
            {highlight?.id ? "Edit Highlights" : "+ Highlights"}
          </Button>
          <Button onClick={() => navigate(`/Auth/election/${EID}`)}>Master settings</Button>
        </div>
      </PageHeader>

      <Container fluid style={{ padding: 16 }}>
        <Tabs
          activeKey={tab}
          onChange={(k) => setTab(k as any)}
          items={[
            { key: "data", label: "Groups · Locations · Candidates" },
            { key: "reports", label: "Reports" },
          ]}
        />

        {tab === "data" ? (
          <Row>
            <Col md={4}>
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #ececec",
                  borderRadius: 10,
                  padding: 14,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingBottom: 10,
                    marginBottom: 8,
                    borderBottom: "1px solid #f4f4f4",
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 600, color: "#1f1f1f", letterSpacing: 0.2 }}>
                    Groups
                  </span>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => setGroupModal({ open: true, parentId: selectedGroupId || null })}
                  >
                    + Group
                  </Button>
                </div>
                {treeData.length ? (
                  <Tree
                    blockNode
                    defaultExpandAll
                    treeData={treeData}
                    selectedKeys={selectedGroupId ? [selectedGroupId] : []}
                    onSelect={(keys) => setSelectedGroupId(keys[0] ? Number(keys[0]) : null)}
                    style={{ background: "transparent" }}
                    titleRender={(node: any) => {
                      const g = groupsFlat.find((x) => x.id === node.key) || {};
                      const isSelected = selectedGroupId === node.key;
                      return (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                            padding: "6px 10px",
                            borderRadius: 6,
                            transition: "background 0.15s",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: isSelected ? 600 : 500,
                              color: isSelected ? "#1677ff" : "#262626",
                              fontSize: 14,
                            }}
                          >
                            {node.title}
                          </span>
                          <span
                            style={{ display: "flex", gap: 10, alignItems: "center" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <BiEdit
                              size={16}
                              color="#f5a442"
                              cursor="pointer"
                              title="Edit group"
                              onClick={(e: any) => {
                                e.stopPropagation();
                                setGroupModal({ open: true, row: g });
                              }}
                            />
                            <Popconfirm
                              title="Move group to trash"
                              description="Children will be detached, not deleted."
                              okText="Yes, move to trash"
                              cancelText="Cancel"
                              okButtonProps={{ danger: true, loading: deletingGroupId === g.id }}
                              onConfirm={(e: any) => {
                                e?.stopPropagation?.();
                                deleteGroup(g.id);
                              }}
                              onCancel={(e: any) => e?.stopPropagation?.()}
                            >
                              <IoTrashOutline
                                size={16}
                                color="#ff4d4f"
                                cursor="pointer"
                                title="Delete group"
                                onClick={(e: any) => e.stopPropagation()}
                              />
                            </Popconfirm>
                          </span>
                        </div>
                      );
                    }}
                  />
                ) : (
                  <div style={{ textAlign: "center", color: "#9aa", padding: "32px 8px", fontSize: 13 }}>
                    No groups yet.
                    <div style={{ fontSize: 12, marginTop: 4, color: "#bbb" }}>
                      Click "+ Group" to add one.
                    </div>
                  </div>
                )}
              </div>
            </Col>

            <Col md={8}>
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #ececec",
                  borderRadius: 10,
                  padding: 14,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingBottom: 10,
                    marginBottom: 8,
                    borderBottom: "1px solid #f4f4f4",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#1f1f1f", letterSpacing: 0.2 }}>
                      Locations
                    </span>
                    {selectedGroupId ? (
                      <span style={{ fontSize: 13, color: "#888" }}>
                        ·{" "}
                        <span style={{ color: "#1677ff", fontWeight: 500 }}>
                          {groupsFlat.find((g) => g.id === selectedGroupId)?.name || `#${selectedGroupId}`}
                        </span>
                      </span>
                    ) : (
                      <span style={{ fontSize: 13, color: "#bbb" }}>(select a group)</span>
                    )}
                  </span>
                  <Button
                    size="small"
                    type="primary"
                    disabled={!selectedGroupId}
                    onClick={() => setLocationModal({ open: true })}
                  >
                    + Location
                  </Button>
                </div>
                {selectedGroupId ? (
                  <Table bordered hover size="sm" style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Voters</th>
                        <th>Polled</th>
                        <th>Status</th>
                        <th>Leader</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {locationsLoading ? (
                        <tr>
                          <td colSpan={7} style={{ textAlign: "center", padding: 24 }}>
                            <Spin /> <span style={{ marginLeft: 8, color: "#888" }}>Loading locations…</span>
                          </td>
                        </tr>
                      ) : locations.length === 0 ? (
                        <tr>
                          <td colSpan={7} style={{ textAlign: "center", color: "#9aa", padding: "32px 8px" }}>
                            No locations in this group.
                            <div style={{ fontSize: 12, marginTop: 4, color: "#bbb" }}>
                              Click "+ Location" to add one.
                            </div>
                          </td>
                        </tr>
                      ) : null}
                      {!locationsLoading && locations.map((l, i) => (
                        <tr key={l.id}>
                          <td>{i + 1}</td>
                          <td style={{ fontWeight: 500 }}>{l.name}</td>
                          <td>{l.total_voters}</td>
                          <td>{l.total_votes_polled}</td>
                          <td>{renderCountingStatus(l.counting_status)}</td>
                          <td>
                            {l.winning_candidate ? (
                              <span>
                                {l.winning_candidate.name}{" "}
                                <Tag color={l.winning_candidate.party?.color}>
                                  {l.winning_candidate.party?.short_name || l.winning_candidate.party?.name}
                                </Tag>
                              </span>
                            ) : (
                              <span style={{ color: "#bbb" }}>—</span>
                            )}
                          </td>
                          <td style={{ display: "flex", gap: 10, alignItems: "center" }}>
                            <Button
                              size="small"
                              type="primary"
                              loading={openingCandsForId === l.id}
                              disabled={openingCandsForId != null && openingCandsForId !== l.id}
                              onClick={() => openCandidates(l)}
                            >
                              Candidates / Votes
                            </Button>
                            <BiEdit
                              size={18}
                              color="#f5a442"
                              cursor="pointer"
                              title="Edit location"
                              onClick={() => setLocationModal({ open: true, row: l })}
                            />
                            <Popconfirm
                              title="Move location to trash"
                              description="Candidates under this location will be removed from reports."
                              okText="Yes, move to trash"
                              cancelText="Cancel"
                              okButtonProps={{ danger: true, loading: deletingLocationId === l.id }}
                              onConfirm={() => deleteLocation(l.id)}
                            >
                              <IoTrashOutline size={18} color="#ff4d4f" cursor="pointer" title="Delete location" />
                            </Popconfirm>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div style={{ textAlign: "center", color: "#9aa", padding: "40px 8px", fontSize: 13 }}>
                    Select a group on the left to see its locations.
                  </div>
                )}
              </div>
            </Col>
          </Row>
        ) : (
          <ReportsTab progress={progress} tally={tally} masterId={EID!} parties={parties} />
        )}
      </Container>

      <GroupModal
        open={groupModal.open}
        row={groupModal.row}
        parents={groupsFlat}
        defaultParentId={groupModal.parentId ?? null}
        confirmLoading={groupSaving}
        onCancel={() => !groupSaving && setGroupModal({ open: false })}
        onOk={saveGroup}
      />

      <LocationModal
        open={locationModal.open}
        row={locationModal.row}
        confirmLoading={locationSaving}
        onCancel={() => !locationSaving && setLocationModal({ open: false })}
        onOk={saveLocation}
      />

      <CandidatesModal
        open={candidatesModal.open}
        location={candidatesModal.location}
        parties={parties}
        masterId={EID!}
        onClose={async (changed: boolean) => {
          setCandidatesModal({ open: false });
          if (changed) {
            await loadLocationsForGroup(selectedGroupId!);
            await Promise.all([loadProgress(), loadTally()]);
          }
        }}
      />

      <HighlightsModal
        open={highlightModal.open}
        row={highlight}
        saving={highlightSaving}
        onCancel={() => !highlightSaving && setHighlightModal({ open: false })}
        onOk={saveHighlight}
      />
    </div>
  );
}

// ── Highlights modal (legacy item: type = "highlight", hardcoded) ───────────
function HighlightsModal({ open, row, saving, onCancel, onOk }: any) {
  const [form] = Form.useForm();
  const isEdit = !!row?.id;

  // Pre-fill on open. Re-runs when `row` changes so a fresh save → reload
  // cycle leaves the modal showing the canonical persisted state next time.
  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }
    if (isEdit) {
      form.setFieldsValue({
        title: row.title || "",
        data: Array.isArray(row.data) ? row.data : [],
      });
    } else {
      form.resetFields();
    }
  }, [open, row, isEdit, form]);

  return (
    <Modal
      title={isEdit ? "Edit Highlights" : "Add Highlights"}
      open={open}
      onCancel={onCancel}
      okText={isEdit ? "Update" : "Save"}
      okButtonProps={{ loading: !!saving }}
      cancelButtonProps={{ disabled: !!saving }}
      maskClosable={!saving}
      keyboard={!saving}
      closable={!saving}
      onOk={() => form.validateFields().then((v) => onOk(v)).catch(() => {})}
      width={760}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: "Required" }]}
        >
          <Input placeholder="Headline / heading" size="large" />
        </Form.Item>
        <HighlightForm key_name="data" party={[]} loading={false} />
      </Form>
    </Modal>
  );
}

// ── Group modal ─────────────────────────────────────────────────────────────
function GroupModal({ open, row, parents, defaultParentId, onCancel, onOk, confirmLoading }: any) {
  const [form] = Form.useForm();
  useEffect(() => {
    if (!open) return;
    if (row?.id) {
      form.setFieldsValue({
        name: row.name,
        slug: row.slug,
        parent: row.parent?.id || row.parent || null,
        sort_order: row.sort_order || 0,
        is_featured: !!row.is_featured,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ parent: defaultParentId ?? null, sort_order: 0, is_featured: false });
    }
  }, [open, row, defaultParentId, form]);

  return (
    <Modal
      title={row?.id ? "Edit group" : "New group"}
      open={open}
      onCancel={onCancel}
      onOk={() => form.validateFields().then((v) => onOk(v))}
      okText={row?.id ? "Update" : "Save"}
      cancelButtonProps={{ disabled: !!confirmLoading }}
      confirmLoading={!!confirmLoading}
      maskClosable={!confirmLoading}
      keyboard={!confirmLoading}
      closable={!confirmLoading}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Name" rules={[{ required: true, message: "Required" }]}>
          <Input />
        </Form.Item>
        <Form.Item name="slug" label="Slug">
          <Input />
        </Form.Item>
        <Form.Item name="parent" label="Parent">
          <Select allowClear placeholder="(root)">
            {(parents || [])
              .filter((p: any) => !row || p.id !== row.id)
              .map((p: any) => (
                <Select.Option key={p.id} value={p.id}>
                  {"— ".repeat(p.depth || 0)}
                  {p.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
        <Form.Item name="sort_order" label="Sort order">
          <Input type="number" />
        </Form.Item>
        <Form.Item
          name="is_featured"
          label="Featured"
          valuePropName="checked"
          tooltip="Surface this group on the election home widget (e.g. featured districts)."
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Location modal ──────────────────────────────────────────────────────────
function LocationModal({ open, row, onCancel, onOk, confirmLoading }: any) {
  const [form] = Form.useForm();
  useEffect(() => {
    if (!open) return;
    if (row?.id) {
      form.setFieldsValue({
        name: row.name,
        code: row.code,
        total_voters: row.total_voters || 0,
        total_votes_polled: row.total_votes_polled || 0,
        counting_status: row.counting_status || "not_started",
        sort_order: row.sort_order || 0,
        is_featured: !!row.is_featured,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        total_voters: 0,
        total_votes_polled: 0,
        counting_status: "not_started",
        sort_order: 0,
        is_featured: false,
      });
    }
  }, [open, row, form]);

  return (
    <Modal
      title={row?.id ? "Edit location" : "New location"}
      open={open}
      onCancel={onCancel}
      onOk={() => form.validateFields().then((v) => onOk(v))}
      okText={row?.id ? "Update" : "Save"}
      cancelButtonProps={{ disabled: !!confirmLoading }}
      confirmLoading={!!confirmLoading}
      maskClosable={!confirmLoading}
      keyboard={!confirmLoading}
      closable={!confirmLoading}
      destroyOnClose
      width={640}
    >
      <Form form={form} layout="vertical">
        <Row>
          <Col sm={8}>
            <Form.Item name="name" label="Name" rules={[{ required: true, message: "Required" }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col sm={4}>
            <Form.Item name="code" label="Code">
              <Input placeholder="AC code, booth no, ..." />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col sm={4}>
            <Form.Item name="total_voters" label="Total voters">
              <Input type="number" />
            </Form.Item>
          </Col>
          <Col sm={4}>
            <Form.Item name="total_votes_polled" label="Votes polled">
              <Input type="number" />
            </Form.Item>
          </Col>
          <Col sm={4}>
            <Form.Item name="counting_status" label="Counting status">
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col sm={4}>
            <Form.Item name="sort_order" label="Sort order">
              <Input type="number" />
            </Form.Item>
          </Col>
          <Col sm={4}>
            <Form.Item
              name="is_featured"
              label="Featured"
              valuePropName="checked"
              tooltip="Surface this constituency in the home-widget featured trio."
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

// ── Candidates + batch votes modal ──────────────────────────────────────────
function CandidatesModal({ open, location, parties, masterId, onClose }: any) {
  const [rows, setRows] = useState<any[]>([]);
  const [status, setStatus] = useState<string>("not_started");
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [creating, setCreating] = useState<{
    name: string;
    partyId: any;
    party_label: string;
    image: string | null;
    symbol: string | null;
    is_featured: boolean;
  }>({
    name: "",
    partyId: null,
    party_label: "",
    image: null,
    symbol: null,
    is_featured: false,
  });
  const [picking, setPicking] = useState<{ rowKey: string | number; field: "image" | "symbol" } | null>(null);
  const [uploading, setUploading] = useState<{ rowKey: string | number; field: "image" | "symbol" } | null>(null);

  const busy = saving || removingId != null;

  useEffect(() => {
    if (open && location) {
      setRows(location.candidates || []);
      setStatus(location.counting_status || "not_started");
    }
  }, [open, location]);

  const updateRow = (i: number, patch: any) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  const removeRow = async (row: any) => {
    if (!row.id) {
      setRows((prev) => prev.filter((r) => r !== row));
      return;
    }
    setRemovingId(row.id);
    try {
      await PUT(API.ELECTION_CANDIDATE_EDIT + row.id, { data: { is_deleted: true } });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      message.success("Candidate removed");
    } catch (err: any) {
      message.error(err?.message || "Failed to remove candidate");
    } finally {
      setRemovingId(null);
    }
  };

  const onPickerSubmit = async (val: any) => {
    if (!picking) return;
    const target = picking;
    setPicking(null);
    setUploading(target);
    let url: string | null = null;
    try {
      if (val?.type === "medialibrary") {
        url = val.val;
      } else if (val?.val?.file) {
        const res: any = await uploadImageToS3(val.val.file);
        url = res?.data?.imageUrl || null;
      }
      if (!url) {
        message.error("Upload failed");
        return;
      }
      if (target.rowKey === "__new") {
        setCreating((c) => ({ ...c, [target.field]: url } as any));
        return;
      }
      const idx = rows.findIndex((r) => (r.id ?? r._tempId) === target.rowKey);
      if (idx < 0) return;
      const r = rows[idx];
      updateRow(idx, { [target.field]: url });
      if (r.id) {
        await PUT(API.ELECTION_CANDIDATE_EDIT + r.id, { data: { [target.field]: url } });
        message.success(`${target.field === "image" ? "Photo" : "Symbol"} updated`);
      }
    } catch (err: any) {
      message.error(err?.message || "Failed to save image");
    } finally {
      setUploading(null);
    }
  };

  const addRow = async () => {
    if (!creating.name) {
      message.warning("Name required");
      return;
    }
    const draft = { ...creating };
    const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const optimistic = {
      _tempId: tempId,
      _saving: true,
      name: draft.name,
      party: parties.find((p: any) => p.id === draft.partyId) || null,
      party_label: draft.party_label || null,
      image: draft.image || null,
      symbol: draft.symbol || null,
      is_featured: !!draft.is_featured,
      votes: 0,
    };
    setRows((prev) => [...prev, optimistic]);
    setCreating({ name: "", partyId: null, party_label: "", image: null, symbol: null, is_featured: false });

    try {
      const payload: any = {
        data: {
          name: draft.name,
          location: location.id,
          election_master: masterId,
          party: draft.partyId || null,
          party_label: draft.party_label || null,
          image: draft.image || null,
          symbol: draft.symbol || null,
          is_featured: !!draft.is_featured,
          votes: "0",
          is_deleted: false,
        },
      };
      const res: any = await POST(API.ELECTION_CANDIDATE_ADD, payload);
      const saved = res?.data?.attributes ? { id: res.data.id, ...res.data.attributes } : res?.data;
      if (saved?.id) {
        setRows((prev) => prev.map((r) => (r._tempId === tempId ? saved : r)));
        message.success("Candidate added");
      } else {
        setRows((prev) => prev.filter((r) => r._tempId !== tempId));
        message.warning("Candidate not saved — please retry");
      }
    } catch (err: any) {
      setRows((prev) => prev.filter((r) => r._tempId !== tempId));
      message.error(err?.message || "Failed to add candidate");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        locationId: location.id,
        counting_status: status,
        candidates: rows
          .filter((r) => r.id)
          .map((r) => ({ id: r.id, votes: r.votes ?? 0 })),
      };
      const res: any = await POST(API.ELECTION_CANDIDATE_BATCH_VOTES, payload);
      if (res?.data) {
        message.success("Votes saved & ranked");
        setRows(res.data);
      }
      onClose(true);
    } catch (err: any) {
      message.error(err?.message || "Failed to save votes");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !location) return null;

  return (
    <Modal
      title={`Candidates · ${location.name}`}
      open={open}
      onCancel={() => !busy && onClose(false)}
      maskClosable={!busy}
      keyboard={!busy}
      closable={!busy}
      footer={[
        <Button key="cancel" disabled={busy} onClick={() => onClose(false)}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} disabled={busy && !saving} onClick={save}>
          Save votes & re-rank
        </Button>,
      ]}
      width={920}
      destroyOnClose
    >
      <Row style={{ marginBottom: 12 }}>
        <Col sm={6}>
          <strong>Counting status</strong>
          <Select
            style={{ width: "100%", marginTop: 4 }}
            value={status}
            onChange={setStatus}
            options={STATUS_OPTIONS}
          />
        </Col>
        <Col sm={6} style={{ textAlign: "right", paddingTop: 18, color: "#888" }}>
          Voters: {location.total_voters} · Polled: {location.total_votes_polled}
        </Col>
      </Row>

      <Table bordered size="sm">
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th style={{ width: 70 }}>Photo</th>
            <th>Party</th>
            <th style={{ width: 70 }}>Symbol</th>
            <th>Label (e.g. INC)</th>
            <th>Votes</th>
            <th>Rank</th>
            <th style={{ width: 80, textAlign: "center" }}>Featured</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const partyId = r.party?.id || r.party || null;
            const rowKey = r.id ?? r._tempId;
            return (
              <tr key={r.id || `new-${i}`}>
                <td>{i + 1}</td>
                <td>
                  <Input
                    bordered={false}
                    value={r.name}
                    onChange={(e) => updateRow(i, { name: e.target.value })}
                    onBlur={async () => {
                      if (r.id) await PUT(API.ELECTION_CANDIDATE_EDIT + r.id, { data: { name: r.name } });
                    }}
                  />
                </td>
                <td style={{ textAlign: "center" }}>
                  <ImageThumbCell
                    value={r.image}
                    loading={uploading?.rowKey === rowKey && uploading?.field === "image"}
                    onPick={() => setPicking({ rowKey, field: "image" })}
                  />
                </td>
                <td>
                  <Select
                    bordered={false}
                    style={{ width: "100%" }}
                    value={partyId}
                    placeholder="—"
                    allowClear
                    onChange={async (val) => {
                      updateRow(i, { party: parties.find((p: any) => p.id === val) || null });
                      if (r.id) await PUT(API.ELECTION_CANDIDATE_EDIT + r.id, { data: { party: val || null } });
                    }}
                  >
                    {parties.map((p: any) => (
                      <Select.Option key={p.id} value={p.id}>
                        <span style={{ display: "inline-block", width: 8, height: 8, background: p.color, borderRadius: 4, marginRight: 6 }} />
                        {p.name}
                      </Select.Option>
                    ))}
                  </Select>
                </td>
                <td style={{ textAlign: "center" }}>
                  <ImageThumbCell
                    value={r.symbol}
                    loading={uploading?.rowKey === rowKey && uploading?.field === "symbol"}
                    onPick={() => setPicking({ rowKey, field: "symbol" })}
                  />
                </td>
                <td>
                  <Input
                    bordered={false}
                    placeholder="INC, IUML, IND..."
                    value={r.party_label || ""}
                    onChange={(e) => updateRow(i, { party_label: e.target.value })}
                    onBlur={async () => {
                      if (r.id) await PUT(API.ELECTION_CANDIDATE_EDIT + r.id, { data: { party_label: r.party_label || null } });
                    }}
                  />
                </td>
                <td>
                  <Input
                    bordered={false}
                    type="number"
                    value={r.votes || 0}
                    onChange={(e) => updateRow(i, { votes: e.target.value })}
                  />
                </td>
                <td>{r.rank || "—"}</td>
                <td style={{ textAlign: "center" }}>
                  <Switch
                    size="small"
                    checked={!!r.is_featured}
                    disabled={!r.id || busy}
                    onChange={async (val) => {
                      updateRow(i, { is_featured: val });
                      if (r.id) {
                        try {
                          await PUT(API.ELECTION_CANDIDATE_EDIT + r.id, { data: { is_featured: val } });
                        } catch (err: any) {
                          updateRow(i, { is_featured: !val });
                          message.error(err?.message || "Failed to update featured flag");
                        }
                      }
                    }}
                  />
                </td>
                <td>
                  {r._saving ? (
                    <Tag color="processing">saving…</Tag>
                  ) : r.is_winner ? (
                    <Tag color="green">winner</Tag>
                  ) : r.rank === 1 ? (
                    <Tag color="blue">leader</Tag>
                  ) : (
                    <Tag>—</Tag>
                  )}
                </td>
                <td>
                  <Popconfirm
                    title="Remove candidate"
                    description={`Remove ${r.name || "this candidate"}? This re-ranks the remaining candidates.`}
                    okText="Yes, remove"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true, loading: removingId === r.id }}
                    onConfirm={() => removeRow(r)}
                  >
                    <Button danger size="small" loading={removingId === r.id}>×</Button>
                  </Popconfirm>
                </td>
              </tr>
            );
          })}
          <tr>
            <td>+</td>
            <td>
              <Input
                placeholder="New candidate name"
                value={creating.name}
                onChange={(e) => setCreating({ ...creating, name: e.target.value })}
              />
            </td>
            <td style={{ textAlign: "center" }}>
              <ImageThumbCell
                value={creating.image}
                loading={uploading?.rowKey === "__new" && uploading?.field === "image"}
                onPick={() => setPicking({ rowKey: "__new", field: "image" })}
              />
            </td>
            <td>
              <Select
                style={{ width: "100%" }}
                value={creating.partyId}
                placeholder="party"
                allowClear
                onChange={(v) => setCreating({ ...creating, partyId: v })}
              >
                {parties.map((p: any) => (
                  <Select.Option key={p.id} value={p.id}>
                    {p.name}
                  </Select.Option>
                ))}
              </Select>
            </td>
            <td style={{ textAlign: "center" }}>
              <ImageThumbCell
                value={creating.symbol}
                loading={uploading?.rowKey === "__new" && uploading?.field === "symbol"}
                onPick={() => setPicking({ rowKey: "__new", field: "symbol" })}
              />
            </td>
            <td>
              <Input
                placeholder="label"
                value={creating.party_label}
                onChange={(e) => setCreating({ ...creating, party_label: e.target.value })}
              />
            </td>
            <td colSpan={2} />
            <td style={{ textAlign: "center" }}>
              <Switch
                size="small"
                checked={!!creating.is_featured}
                onChange={(val) => setCreating({ ...creating, is_featured: val })}
              />
            </td>
            <td />
            <td>
              <Button size="small" type="primary" disabled={!creating.name} onClick={addRow}>
                Add
              </Button>
            </td>
          </tr>
        </tbody>
      </Table>

      {picking ? (
        <ImagePicker2
          visible={!!picking}
          close={() => setPicking(null)}
          onSubmit={onPickerSubmit}
        />
      ) : null}
    </Modal>
  );
}

function ImageThumbCell({
  value,
  loading,
  onPick,
}: {
  value?: string | null;
  loading?: boolean;
  onPick: () => void;
}) {
  if (loading) {
    return (
      <div
        style={{
          width: 32,
          height: 32,
          margin: "0 auto",
          borderRadius: 4,
          background: "#fafafa",
          border: "1px dashed #d9d9d9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="small" />
      </div>
    );
  }
  if (value) {
    return (
      <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <img
          src={value}
          alt=""
          style={{ width: 32, height: 32, borderRadius: 4, objectFit: "cover", cursor: "pointer", border: "1px solid #eee" }}
          onClick={onPick}
        />
        <BiEdit size={14} color="#f5a442" cursor="pointer" onClick={onPick} />
      </div>
    );
  }
  return (
    <div
      onClick={onPick}
      style={{
        width: 32,
        height: 32,
        margin: "0 auto",
        borderRadius: 4,
        background: "#fafafa",
        border: "1px dashed #d9d9d9",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#bbb",
        cursor: "pointer",
        fontSize: 16,
        lineHeight: 1,
      }}
    >
      +
    </div>
  );
}

// ── Reports tab ─────────────────────────────────────────────────────────────
function ReportsTab({ progress, tally: tallyInit, masterId, parties }: any) {
  const PAGE_SIZE = 10;
  const [view, setView] = useState<"tally" | "by-location" | "by-party">("tally");
  const [partyId, setPartyId] = useState<number | null>(null);

  const [tally, setTally] = useState<any[]>(tallyInit || []);
  const [tallyTotal, setTallyTotal] = useState<number>((tallyInit || []).length);
  const [tallyPage, setTallyPage] = useState(1);
  const [tallyLoading, setTallyLoading] = useState(false);

  const [byLocation, setByLocation] = useState<any[]>([]);
  const [byLocationTotal, setByLocationTotal] = useState(0);
  const [byLocationPage, setByLocationPage] = useState(1);
  const [byLocationLoading, setByLocationLoading] = useState(false);

  const [byParty, setByParty] = useState<any[]>([]);
  const [byPartyTotal, setByPartyTotal] = useState(0);
  const [byPartyPage, setByPartyPage] = useState(1);
  const [byPartyLoading, setByPartyLoading] = useState(false);

  useEffect(() => {
    setTally(tallyInit || []);
    setTallyTotal((tallyInit || []).length);
  }, [tallyInit]);

  // reset to page 1 when the underlying party filter changes
  useEffect(() => {
    setByPartyPage(1);
  }, [partyId]);

  useEffect(() => {
    if (view === "tally") {
      setTallyLoading(true);
      GET(
        API.ELECTION_REPORT_TALLY +
          `?masterId=${masterId}&start=${(tallyPage - 1) * PAGE_SIZE}&limit=${PAGE_SIZE}`,
        null,
        null
      )
        .then((res: any) => {
          setTally(res?.data || []);
          setTallyTotal(res?.total ?? (res?.data?.length || 0));
        })
        .finally(() => setTallyLoading(false));
    }
    if (view === "by-location") {
      setByLocationLoading(true);
      GET(
        API.ELECTION_REPORT_BY_LOCATION +
          `?masterId=${masterId}&top=3&start=${(byLocationPage - 1) * PAGE_SIZE}&limit=${PAGE_SIZE}`,
        null,
        null
      )
        .then((res: any) => {
          setByLocation(res?.data || []);
          setByLocationTotal(res?.total ?? (res?.data?.length || 0));
        })
        .finally(() => setByLocationLoading(false));
    }
    if (view === "by-party" && partyId) {
      setByPartyLoading(true);
      GET(
        API.ELECTION_REPORT_BY_PARTY +
          `?masterId=${masterId}&partyId=${partyId}&start=${(byPartyPage - 1) * PAGE_SIZE}&limit=${PAGE_SIZE}`,
        null,
        null
      )
        .then((res: any) => {
          setByParty(res?.data || []);
          setByPartyTotal(res?.total ?? (res?.data?.length || 0));
        })
        .finally(() => setByPartyLoading(false));
    }
  }, [view, partyId, masterId, tallyPage, byLocationPage, byPartyPage]);

  const cardStyle: React.CSSProperties = {
    background: "#fff",
    border: "1px solid #ececec",
    borderRadius: 10,
    padding: 14,
    boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
    marginBottom: 14,
  };
  const cardHeaderStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 10,
    marginBottom: 10,
    borderBottom: "1px solid #f4f4f4",
  };
  const cardTitleStyle: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 600,
    color: "#1f1f1f",
    letterSpacing: 0.2,
  };

  const completed = progress?.completed ?? 0;
  const totalLocations = progress?.totalLocations ?? 0;
  const percent = progress?.percent ?? 0;
  const turnout = progress?.turnoutPercent ?? 0;

  return (
    <div>
      {/* Summary: progress + tabs */}
      <div style={cardStyle}>
        <Row align="middle">
          <Col md={5}>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>
              Counting progress
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6 }}>
              <span style={{ fontSize: 26, fontWeight: 700, color: "#1f1f1f", lineHeight: 1 }}>
                {completed}
              </span>
              <span style={{ fontSize: 14, color: "#888" }}>/ {totalLocations}</span>
              <Tag color={percent >= 100 ? "success" : "processing"} style={{ marginLeft: 4 }}>
                {percent}%
              </Tag>
            </div>
            <div
              style={{
                marginTop: 8,
                height: 6,
                width: "100%",
                background: "#f0f0f0",
                borderRadius: 3,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.min(100, percent)}%`,
                  height: "100%",
                  background: percent >= 100 ? "#52c41a" : "#1677ff",
                  transition: "width 0.3s",
                }}
              />
            </div>
            <div style={{ color: "#888", fontSize: 12, marginTop: 8 }}>
              Polled {progress?.totalPolled ?? 0} of {progress?.totalVoters ?? 0} ·{" "}
              <span style={{ color: "#1677ff", fontWeight: 500 }}>{turnout}% turnout</span>
            </div>
          </Col>
          <Col md={7}>
            <Tabs
              activeKey={view}
              onChange={(k) => setView(k as any)}
              items={[
                { key: "tally", label: "Seat tally" },
                { key: "by-location", label: "By location" },
                { key: "by-party", label: "By party" },
              ]}
              tabBarStyle={{ marginBottom: 0 }}
            />
          </Col>
        </Row>
      </div>

      {view === "tally" ? (
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <span style={cardTitleStyle}>Seat tally</span>
            <span style={{ fontSize: 12, color: "#888" }}>
              {tallyTotal} {tallyTotal === 1 ? "party" : "parties"}
            </span>
          </div>
          <Table bordered hover size="sm" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th>Party</th>
                <th style={{ textAlign: "center", width: 110 }}>Leading</th>
                <th style={{ textAlign: "center", width: 90 }}>Won</th>
                <th style={{ textAlign: "center", width: 90 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {tallyLoading ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", padding: 24 }}>
                    <Spin /> <span style={{ marginLeft: 8, color: "#888" }}>Loading seat tally…</span>
                  </td>
                </tr>
              ) : tally.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", color: "#9aa", padding: "32px 8px" }}>
                    No parties yet.
                  </td>
                </tr>
              ) : (
                tally.map((t: any, i: number) => (
                  <tr key={i}>
                    <td>
                      <span
                        style={{
                          display: "inline-block",
                          width: 12,
                          height: 12,
                          background: t.color,
                          borderRadius: 6,
                          marginRight: 8,
                          verticalAlign: "middle",
                        }}
                      />
                      <span style={{ fontWeight: 500 }}>{t.name}</span>
                      {t.alliance ? (
                        <Tag style={{ marginLeft: 8, fontSize: 11 }}>{t.alliance}</Tag>
                      ) : null}
                    </td>
                    <td style={{ textAlign: "center", color: t.leading > 0 ? "#1677ff" : "#bbb", fontWeight: t.leading > 0 ? 500 : 400 }}>
                      {t.leading}
                    </td>
                    <td style={{ textAlign: "center", color: t.won > 0 ? "#52c41a" : "#bbb", fontWeight: t.won > 0 ? 500 : 400 }}>
                      {t.won}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <strong style={{ color: t.total > 0 ? "#1f1f1f" : "#bbb" }}>{t.total}</strong>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          {tallyTotal > PAGE_SIZE ? (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <Pagination
                current={tallyPage}
                pageSize={PAGE_SIZE}
                total={tallyTotal}
                showSizeChanger={false}
                onChange={setTallyPage}
                size="small"
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {view === "by-location" ? (
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <span style={cardTitleStyle}>By location</span>
            <span style={{ fontSize: 12, color: "#888" }}>
              {byLocationTotal} {byLocationTotal === 1 ? "location" : "locations"}
            </span>
          </div>
          <Table bordered hover size="sm" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th>Location</th>
                <th>Group</th>
                <th>Leader</th>
                <th style={{ textAlign: "center", width: 100 }}>Margin</th>
                <th style={{ width: 130 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {byLocationLoading ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: 24 }}>
                    <Spin /> <span style={{ marginLeft: 8, color: "#888" }}>Loading by-location report…</span>
                  </td>
                </tr>
              ) : byLocation.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", color: "#9aa", padding: "32px 8px" }}>
                    No locations to report.
                  </td>
                </tr>
              ) : (
                byLocation.map((l: any) => (
                  <tr key={l.id}>
                    <td style={{ fontWeight: 500 }}>{l.name}</td>
                    <td>{l.group?.name || <span style={{ color: "#bbb" }}>—</span>}</td>
                    <td>
                      {l.leader ? (
                        <span>
                          {l.leader.name}{" "}
                          <Tag color={l.leader.party?.color}>
                            {l.leader.party?.short_name || l.leader.party?.name}
                          </Tag>
                        </span>
                      ) : (
                        <span style={{ color: "#bbb" }}>—</span>
                      )}
                    </td>
                    <td style={{ textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
                      {l.leader?.lead_margin || 0}
                    </td>
                    <td>{renderCountingStatus(l.counting_status)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          {byLocationTotal > PAGE_SIZE ? (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <Pagination
                current={byLocationPage}
                pageSize={PAGE_SIZE}
                total={byLocationTotal}
                showSizeChanger={false}
                onChange={setByLocationPage}
                size="small"
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {view === "by-party" ? (
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <span style={cardTitleStyle}>By party</span>
            <Select
              style={{ width: 280 }}
              placeholder="Select a party"
              value={partyId}
              onChange={setPartyId}
              showSearch
              optionFilterProp="children"
            >
              {(parties || []).map((p: any) => (
                <Select.Option key={p.id} value={p.id}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      background: p.color,
                      borderRadius: 4,
                      marginRight: 6,
                    }}
                  />
                  {p.name}
                </Select.Option>
              ))}
            </Select>
          </div>
          <Table bordered hover size="sm" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Location</th>
                <th style={{ textAlign: "center", width: 100 }}>Votes</th>
                <th style={{ textAlign: "center", width: 80 }}>Rank</th>
                <th style={{ width: 100 }}>Result</th>
              </tr>
            </thead>
            <tbody>
              {!partyId ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", color: "#9aa", padding: "32px 8px" }}>
                    Select a party to see its candidates.
                  </td>
                </tr>
              ) : byPartyLoading ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: 24 }}>
                    <Spin /> <span style={{ marginLeft: 8, color: "#888" }}>Loading by-party report…</span>
                  </td>
                </tr>
              ) : byParty.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", color: "#9aa", padding: "32px 8px" }}>
                    No candidates for this party.
                  </td>
                </tr>
              ) : (
                byParty.map((row: any) => (
                  <tr key={row.candidate.id}>
                    <td style={{ fontWeight: 500 }}>{row.candidate.name}</td>
                    <td>{row.location?.name || <span style={{ color: "#bbb" }}>—</span>}</td>
                    <td style={{ textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
                      {row.candidate.votes}
                    </td>
                    <td style={{ textAlign: "center" }}>{row.candidate.rank}</td>
                    <td>
                      {row.candidate.is_winner ? (
                        <Tag color="green">Won</Tag>
                      ) : row.candidate.rank === 1 ? (
                        <Tag color="blue">Leading</Tag>
                      ) : (
                        <Tag>—</Tag>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          {partyId && byPartyTotal > PAGE_SIZE ? (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <Pagination
                current={byPartyPage}
                pageSize={PAGE_SIZE}
                total={byPartyTotal}
                showSizeChanger={false}
                onChange={setByPartyPage}
                size="small"
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default ElectionDataScreen;
